import React, { useState } from 'react';
import { fetchReconciliation } from '../services/api';
import { toTitleCase } from '../utils/format';

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 0, label: 'Open' },
  { value: 1, label: 'Closed' },
  { value: 2, label: 'In Progress' },
];

export default function Reconciliation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [fields, setFields] = useState({
    orderId: '', invoiceNumber: '', trackingNumber: '',
    firstName: '', lastName: '', shippingMethod: '',
    status: '', requestedFrom: '', requestedTo: '',
    actualFrom: '', actualTo: '',
  });

  const set = (k, v) => setFields(f => ({ ...f, [k]: v }));

  const load = (p = 1) => {
    setLoading(true);
    setError(null);
    setPage(p);
    const params = {
      PageNumber: p,
      PageSize: PAGE_SIZE,
      ...(fields.orderId ? { OrderId: fields.orderId } : {}),
      ...(fields.invoiceNumber ? { ShipInvoiceNumber: fields.invoiceNumber } : {}),
      ...(fields.trackingNumber ? { TrackingNumber: fields.trackingNumber } : {}),
      ...(fields.firstName ? { CustomerFirstName: fields.firstName } : {}),
      ...(fields.lastName ? { CustomerLastName: fields.lastName } : {}),
      ...(fields.shippingMethod ? { ShippingMethod: fields.shippingMethod } : {}),
      ...(fields.status !== '' ? { Status: Number(fields.status) } : {}),
      ...(fields.requestedFrom ? { RequestedDateFrom: toApiDate(fields.requestedFrom) } : {}),
      ...(fields.requestedTo ? { RequestedDateTo: toApiDate(fields.requestedTo) } : {}),
      ...(fields.actualFrom ? { ActualDateFrom: toApiDate(fields.actualFrom) } : {}),
      ...(fields.actualTo ? { ActualDateTo: toApiDate(fields.actualTo) } : {}),
    };
    fetchReconciliation(params)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  const rows = Array.isArray(data) ? data : data?.data ?? data?.records?.data ?? [];
  const total = data?.totalRows ?? data?.total ?? rows.length;

  const statusLabel = (s) => {
    const m = { 0: 'Open', 1: 'Closed', 2: 'In Progress' };
    return m[s] ?? String(s ?? 'Unknown');
  };
  const statusStyle = (s) => {
    if (s === 1) return { bg: '#e6f9f0', color: '#1a8a4a' };
    if (s === 2) return { bg: '#e8f0fe', color: '#00395C' };
    return { bg: '#fff8e6', color: '#b06a00' };
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Reconciliation</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Search Filters</div>
        <div style={{ padding: '12px 16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
            {[
              { key: 'orderId', label: 'Order ID' },
              { key: 'invoiceNumber', label: 'Invoice #' },
              { key: 'trackingNumber', label: 'Tracking #' },
              { key: 'firstName', label: 'First Name' },
              { key: 'lastName', label: 'Last Name' },
              { key: 'shippingMethod', label: 'Shipping Method' },
            ].map(({ key, label }) => (
              <div key={key}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>{label.toUpperCase()}</div>
                <input type="text" value={fields[key]} onChange={e => set(key, e.target.value)} placeholder={label} className="form-input" style={{ fontSize: 11, width: '100%' }} />
              </div>
            ))}
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>STATUS</div>
              <select value={fields.status} onChange={e => set('status', e.target.value)} className="form-select" style={{ fontSize: 11, width: '100%' }}>
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
            {[
              { key: 'requestedFrom', label: 'Requested From' },
              { key: 'requestedTo', label: 'Requested To' },
              { key: 'actualFrom', label: 'Actual From' },
              { key: 'actualTo', label: 'Actual To' },
            ].map(({ key, label }) => (
              <div key={key}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>{label.toUpperCase()}</div>
                <input type="date" value={fields[key]} onChange={e => set(key, e.target.value)} className="form-input" style={{ fontSize: 11, width: '100%' }} />
              </div>
            ))}
          </div>
          <button onClick={() => load(1)} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
        </div>
      </div>

      {data !== null && (
        <div className="card">
          <div className="card-header">Results {total > 0 && `(${total.toLocaleString()})`}</div>
          {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
          {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
          {!loading && !error && rows.length === 0 && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No records found.</div>
          )}
          {rows.length > 0 && (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                      <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Invoice #</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Tracking #</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Requested</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Actual</th>
                      <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const ss = statusStyle(row.status);
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                          <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{(row.bcOrderId && String(row.bcOrderId) !== '0' ? row.bcOrderId : row.orderId) ?? '—'}</td>
                          <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>
                            {toTitleCase([row.customerFirstName, row.customerLastName].filter(Boolean).join(' ') || row.customerName || '—')}
                          </td>
                          <td style={{ padding: '8px 8px', color: 'var(--gray-500)', fontSize: 11 }}>{row.shipInvoiceNumber ?? '—'}</td>
                          <td style={{ padding: '8px 8px', color: 'var(--gray-500)', fontSize: 11 }}>{row.trackingNumber ?? '—'}</td>
                          <td style={{ padding: '8px 8px', color: 'var(--gray-500)' }}>
                            {row.requestedDateFrom ?? row.requestedDate ? new Date(row.requestedDateFrom ?? row.requestedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          </td>
                          <td style={{ padding: '8px 8px', color: 'var(--gray-500)' }}>
                            {row.actualDateFrom ?? row.actualDate ? new Date(row.actualDateFrom ?? row.actualDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          </td>
                          <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: ss.bg, color: ss.color }}>
                              {statusLabel(row.status)}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px', color: 'var(--gray-700)' }}>{row.shippingMethod ?? row.shipType ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {total > PAGE_SIZE && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                  <button onClick={() => load(Math.max(1, page - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>← Prev</button>
                  <span style={{ fontSize: 11, alignSelf: 'center' }}>Page {page}</span>
                  <button onClick={() => load(page + 1)} disabled={rows.length < PAGE_SIZE} className="btn btn-navy" style={{ fontSize: 11 }}>Next →</button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
